import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {EstadoOnboardingPainel, OnboardingPainelService} from '../onboarding-painel.service';
import {FormasDePagamentoService} from '../../services/formas-de-pagamento.service';
import {OnbFocoService} from '../apoio/onb-foco.service';

interface GrupoDePagamento {
  chave: string;
  nome: string;
  metodo: string;
  detalhe: string;
  formas: any[];
  pix: boolean;
  chavePix: string;
  salvando: boolean;
  erro: string;
}

const ORDEM_METODOS = ['DINHEIRO', 'PIX', 'CREDITO', 'DEBITO', 'VALE_REFEICAO', 'VALE_ALIMENTACAO', 'CARTEIRA_DIGITAL', 'CARTAO_PRESENTE', 'OUTRO'];

const DETALHES: Record<string, string> = {
  DINHEIRO: 'O cliente informa se precisa de troco.',
  PIX: 'O cliente paga pela sua chave e manda o comprovante.',
  CREDITO: 'Na maquininha, na entrega ou no balcão.',
  DEBITO: 'Na maquininha, na entrega ou no balcão.',
  VALE_REFEICAO: 'Alelo, Sodexo, VR, Ticket…',
  VALE_ALIMENTACAO: 'Cartões de alimentação.'
};

const ICONES: Record<string, string> = {
  DINHEIRO: 'fas fa-money-bill-wave', PIX: 'fas fa-qrcode', CREDITO: 'far fa-credit-card', DEBITO: 'far fa-credit-card',
  VALE_REFEICAO: 'fas fa-utensils', VALE_ALIMENTACAO: 'fas fa-shopping-basket'
};

/**
 * Passo 4 · Pagamento. As formas padrão já nascem ligadas: o dono só desliga o que não aceita e informa a chave
 * Pix. Usa o mesmo serviço da tela Formas de pagamento; as bandeiras de uma forma ligam e desligam juntas.
 */
@Component({
  selector: 'app-passo-pagamento',
  templateUrl: './passo-pagamento.component.html',
  styleUrls: ['./passo-pagamento.component.scss']
})
export class PassoPagamentoComponent implements OnInit {
  @Input() estado: EstadoOnboardingPainel;
  @Output() concluido = new EventEmitter<EstadoOnboardingPainel>();

  grupos: GrupoDePagamento[] = [];
  carregando = true;
  confirmando = false;
  erro = '';

  constructor(private formas: FormasDePagamentoService, private onboarding: OnboardingPainelService, private foco: OnbFocoService) {}

  ngOnInit(): void { this.carregue(); }

  carregue(): void {
    this.carregando = true;
    this.erro = '';
    this.formas.listeFormasPagamentosLoja().then((dados: any) => {
      // marketplaces (iFood, 99Food, Keeta...) têm forma própria e não fazem sentido para quem está começando
      const deMarketplace = (f: any) => /ifood|99\s?food|keeta|rappi|aiqfome/i.test(`${f?.formaDePagamentoPdv?.nome || ''} ${f?.descricao || ''} ${f?.nome || ''}`);
      const offline: any[] = (dados?.offline || [])
        .filter((f: any) => f?.formaDePagamentoPdv?.tipo !== 'FIDELIDADE' && !f.online && !deMarketplace(f));
      const porChave = new Map<string, GrupoDePagamento>();
      for (const f of offline) {
        const pdv = f.formaDePagamentoPdv || {};
        const chave = String(pdv.id || f.nome || f.descricao);
        if (!porChave.has(chave)) {
          const metodo = String(pdv.metodo || 'OUTRO').toUpperCase();
          const pix = metodo === 'PIX' && String(pdv.tipo || '').toUpperCase() === 'MANUAL';
          // dois "Pix" confundem: o manual é o da chave da loja; o outro é o da maquininha
          const nome = metodo === 'PIX' ? (pix ? 'Pix pela chave da loja' : 'Pix na maquininha') : (pdv.nome || f.descricao || f.nome);
          const detalhe = metodo === 'PIX' && !pix ? 'O cliente paga no Pix da maquininha, na entrega ou no balcão.' : (DETALHES[metodo] || '');
          porChave.set(chave, {chave, metodo, nome, detalhe, formas: [], pix, chavePix: '', salvando: false, erro: ''});
        }
        const grupo = porChave.get(chave);
        grupo.formas.push(f);
        if (!grupo.chavePix && f.chavePix) grupo.chavePix = f.chavePix;
      }
      this.grupos = Array.from(porChave.values())
        .sort((a, b) => ORDEM_METODOS.indexOf(a.metodo) - ORDEM_METODOS.indexOf(b.metodo));
    }).catch((e: any) => this.erro = typeof e === 'string' ? e : 'Não foi possível carregar as formas de pagamento.')
      .finally(() => this.carregando = false);
  }

  icone(g: GrupoDePagamento): string { return ICONES[g.metodo] || 'fas fa-wallet'; }

  ligado(g: GrupoDePagamento): boolean { return g.formas.some(f => f.exibirCardapio); }

  get algumLigado(): boolean { return this.grupos.some(g => this.ligado(g)); }

  get pixSemChave(): boolean { return this.grupos.some(g => g.pix && this.ligado(g) && !g.chavePix.trim()); }

  async alterne(g: GrupoDePagamento, ligar: boolean): Promise<void> {
    if (g.salvando) return;
    if (g.pix && ligar && !g.chavePix.trim()) {
      g.erro = 'Informe a chave Pix abaixo para ligar o Pix.';
      g.formas.forEach(f => f.exibirCardapio = false);
      return;
    }
    g.salvando = true;
    g.erro = '';
    try {
      for (const f of g.formas) {
        if (!!f.exibirCardapio === ligar && f.id) continue;
        f.exibirCardapio = ligar;
        if (g.pix) f.chavePix = g.chavePix.trim();
        if (ligar) {
          const salva: any = await this.formas.ativeFormaPagamentoNaEmpresa(f);
          if (!f.id && salva?.id) f.id = salva.id;
        } else if (f.id) {
          await this.formas.desativeFormaPagamentoNaEmpresa(f);
        }
      }
    } catch (e) {
      g.formas.forEach(f => f.exibirCardapio = !ligar);
      g.erro = typeof e === 'string' ? e : 'Não foi possível salvar. Tente de novo.';
    } finally {
      g.salvando = false;
    }
  }

  async salveChavePix(g: GrupoDePagamento): Promise<void> {
    const chave = g.chavePix.trim();
    if (!chave) return;
    g.salvando = true;
    g.erro = '';
    try {
      for (const f of g.formas) {
        f.chavePix = chave;
        if (f.id) await this.formas.salveChavePix(f);
      }
      if (!this.ligado(g)) await this.alterne(g, true);
    } catch (e) {
      g.erro = typeof e === 'string' ? e : 'Não foi possível salvar a chave.';
    } finally {
      g.salvando = false;
    }
  }

  confirme(): void {
    if (this.confirmando || this.carregando) return;
    if (!this.algumLigado) return this.foco.mostre('onb-pagamentos', 'Ligue pelo menos uma forma de pagamento que você aceita.');
    const pix = this.grupos.find(g => g.pix && this.ligado(g) && !g.chavePix.trim());
    if (pix) return this.foco.mostre('onb-chave-' + pix.chave, 'Informe sua chave Pix para o cliente ver a opção Pix, ou desligue o Pix.');
    this.confirmando = true;
    this.erro = '';
    this.onboarding.confirme('pagamento').then(estado => this.concluido.emit(estado))
      .catch(e => this.erro = OnboardingPainelService.mensagem(e))
      .finally(() => this.confirmando = false);
  }
}
